/**
 * Residual TARA v2 — state + data types.
 *
 * The backend (`GET /api/v1/residual/{sessionId}`) owns the phase
 * state machine. The frontend mirrors `current_phase` and the
 * per-phase record arrays (controls / requirements / attack_paths /
 * risks). Treat all record fields as nullable — phases later in the
 * pipeline don't populate them until the agent has run.
 */

export type ResidualPhase =
  | 'controls_pending'           // user editable — Controls Implementation
  | 'requirements_evaluation'    // agent running — spinner
  | 'requirements_review'        // user editable — Requirements Review
  | 'attack_path_analysis'       // agent running — spinner
  | 'risk_analysis'              // agent running — spinner (auto-started)
  | 'risk_decisions'             // user editable — Risk Decisions
  | 'completed';                 // terminal — Completion

/** Loose shape — the backend returns plenty of optional fields per
 *  record. Each phase component narrows down to what it needs.    */
export interface ResidualControl {
  control_id: string;
  control_name?: string;
  control_description?: string;
  source_claim_id?: string;
  implementation_status?: 'pending' | 'implemented' | string;
  implementation_date?: string;
  implemented_by?: string;
  implementation_notes?: string;
  /** Backend writes evidence as an array of `{ filename, content,
   *  source, … }` objects or `null`. Older paths may still write a
   *  string; treat as opaque. */
  evidence?: unknown;
  github_evidence_collected?: boolean;
  requirements?: ResidualRequirement[];
  [key: string]: unknown;
}

export interface ResidualRequirement {
  requirement_id: string;
  req_id?: string;
  requirement_text?: string;
  linked_control_ids?: string[];
  fulfillment_status?: 'fulfilled' | 'partial' | 'not_fulfilled' | 'pending' | string;
  agent_rationale?: string;
  /** What the verdict was based on — evidence document/section, "implementation
   *  note", or "none". Lets a reviewer see why a requirement passed or failed. */
  evidence_basis?: string;
  /** Set when a positive verdict had no evidence documents to rest on. */
  needs_review?: boolean;
  user_override?: string | null;
  [key: string]: unknown;
}

export interface ResidualAttackPath {
  attack_path_id: string;
  threat_id?: string;
  threat_name?: string;
  asset_id?: string;
  asset_name?: string;
  previous_feasibility_rating?: string;
  previous_feasibility_score?: number;
  current_feasibility_rating?: string;
  current_feasibility_score?: number;
  contributing_control_ids?: string[];
  rationale?: string;
  [key: string]: unknown;
}

export interface ResidualRisk {
  risk_id: string;
  threat_id?: string;
  threat_name?: string;
  asset_id?: string;
  asset_name?: string;
  attack_path_id?: string;
  impact_rating?: string;
  previous_risk_rating?: string;
  previous_risk_score?: number;
  current_risk_rating?: string;
  current_risk_score?: number;
  mitigating_control_ids?: string[];
  recommended_treatment?: string;
  current_treatment?: 'Avoid' | 'Reduce' | 'Share' | 'Retain' | string | null;
  decision_justification?: string;
  [key: string]: unknown;
}

export interface ResidualData {
  current_phase: ResidualPhase;
  controls?: ResidualControl[];
  requirements?: ResidualRequirement[];
  attack_paths?: ResidualAttackPath[];
  risks?: ResidualRisk[];
  new_claims?: unknown[];
  base_tara_summary?: Record<string, unknown>;
  /** Persisted by the backend when an analysis-phase agent run fails;
   *  cleared server-side by POST /continue (retry). */
  error_message?: string | null;
  [key: string]: unknown;
}

export interface EvidenceStatus {
  collection_status?: string;
  controls_processed?: number;
  total_evidence?: number;
  [key: string]: unknown;
}

/** Reducer state — one slice per concern.
 *  - `selectedStop` is which rail stop the user is currently viewing
 *    (null until first hydrate, then defaults to the backend's
 *    active stop).
 *  - `backendPhase` is the raw phase string the backend last
 *    reported. The "active rail stop" is derived from it via
 *    PHASE_TO_RAIL_STOP. */
export interface ResidualState {
  selectedStop: RailStop | null;
  backendPhase: ResidualPhase | null;
  data: ResidualData | null;
  evidenceStatus: EvidenceStatus | null;
  /** Set when the backend transitions back to `controls_pending`
   *  after we'd already left the risk-decisions phase — drives the
   *  LoopBackBanner (Commit 10). */
  loopBackPending: boolean;
  loading: boolean;
  error: string | null;
  /** Backend-persisted agent failure (`error_message` on the progress
   *  document). Distinct from `error` (fetch/transport failures, which
   *  drive the page banner + init guard): this one renders a retry
   *  card inside the affected analysis phase. */
  analysisError: string | null;
}

export const initialResidualState: ResidualState = {
  selectedStop: null,
  backendPhase: null,
  data: null,
  evidenceStatus: null,
  loopBackPending: false,
  loading: false,
  error: null,
  analysisError: null,
};

/** Phases where the agent is running and the user can only watch. */
export const ANALYSIS_PHASES: readonly ResidualPhase[] = [
  'requirements_evaluation',
  'attack_path_analysis',
  'risk_analysis',
] as const;

/* ────────────────────────────────────────────────────────────────
   Rail stops — what the user navigates in the left rail.
   Backend has seven phases; the UI surfaces five stops. Risk scoring
   (`risk_analysis`) and risk treatment (`risk_decisions`) collapse
   onto a single "Residual Risks & Decisions" stop — the agent-running
   scoring renders a spinner inside that stop's body, and the
   treatment chooser opens once the backend reaches `risk_decisions`.
   The two other agent-running phases (`requirements_evaluation`,
   `attack_path_analysis`) collapse onto their adjacent user-facing
   stop the same way.
   ──────────────────────────────────────────────────────────────── */
export type RailStop =
  | 'controls'
  | 'requirements'
  | 'attack_paths'
  | 'risks'
  | 'completion';

export const RAIL_STOPS: readonly RailStop[] = [
  'controls',
  'requirements',
  'attack_paths',
  'risks',
  'completion',
] as const;

export const RAIL_STOP_LABEL: Record<RailStop, string> = {
  controls: 'Controls Implementation',
  requirements: 'Requirements Review',
  attack_paths: 'Residual Attack Paths',
  risks: 'Residual Risks & Decisions',
  completion: 'Completion',
};

/** Backend phase → rail stop. Used to derive the "active" rail
 *  stop from `backendPhase` and to decide which stops are
 *  click-to-jumpable (≤ active). Both `risk_analysis` and
 *  `risk_decisions` map to the merged `risks` stop. */
export const PHASE_TO_RAIL_STOP: Record<ResidualPhase, RailStop> = {
  controls_pending: 'controls',
  requirements_evaluation: 'requirements',
  requirements_review: 'requirements',
  attack_path_analysis: 'attack_paths',
  risk_analysis: 'risks',
  risk_decisions: 'risks',
  completed: 'completion',
};

/** Index of a rail stop within `RAIL_STOPS` (used for click-to-jump
 *  guard: only stops with index ≤ activeIndex are clickable). */
export const railStopIndex = (stop: RailStop): number =>
  RAIL_STOPS.indexOf(stop);
